import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { Reset } from 'styled-reset';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import { createSelector } from 'reselect';
import { SplitFactory, withSplitFactory } from '@splitsoftware/splitio-react';
import { ThemeProvider, Application, Icon, Div, Image, Text } from '@pinpoint/design-kit';
import * as Sentry from '@sentry/browser';
import { ProductFruits } from 'react-product-fruits';

import DefaultTheme from 'src/styles/DefaultTheme';

import { createGlobalStyle } from 'styled-components';
import { hotjar } from 'react-hotjar';

import GLStyle from 'src/styles';
import { inlineLogout } from 'src/store/api';
import { appActions } from 'src/store/actions';
import { ProtectedRoute } from 'src/components/hoc';
import CONSTANTS from 'src/store/constants';
import { BreadcrumbSwitch, BreadcrumbRoute, ClientsSelection, ErrorBoundary, Feedback, LoginSSO } from 'src/components';
import * as Pages from 'src/pages';
import { Breadcrumbs } from 'src/containers';

import { ROUTES } from 'src/config';
import TokenExchange from 'src/components/TokenExchange';

const GlobalStyle = createGlobalStyle`
  @media (max-width: ${DefaultTheme.grid.breakpoints.lg}px) {
    html {
      font-size: 14px;
    }
  }
  @media (min-width: ${DefaultTheme.grid.breakpoints.lg}px) {
    html {
      font-size: 16px;
    }
  }
  body {
    font-family: Roboto;
    margin: 0;
    padding: 0;
  }
`;

const HJID = process.env.REACT_APP_HJID || '2142042';
const HJSV = process.env.REACT_APP_HJSV || '6';

const SPLITIO_CONFIG = {
  core: {
    authorizationKey: process.env.REACT_APP_SPLITIO_AUTHORIZATION_KEY || 'localhost',
    key: process.env.REACT_APP_SPLITIO_CUSTOMER_ID || 'CUSTOMER_ID',
  },
  // features is only enabled during local development when authorization key is 'localhost'
  features: {
    [CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS]: 'on',
    [CONSTANTS.SPLIT_NAMES.CAN_VIEW_THINKALIKE_MODELS]: 'on',
    [CONSTANTS.SPLIT_NAMES.CAN_VIEW_ZIP_CODE_RANKING]: 'off',
  },
};

if (process.env.REACT_APP_ENV) {
  // initialize hotjar only in staging
  // and production
  hotjar.initialize(HJID, HJSV);
}

const menu = [
  {
    type: 'ITEM',
    icon: 'Company',
    name: 'Clients',
    color: 'blue',
    link: '/clients',
    isHighlighted: (pathname) => pathname.startsWith('/clients'),
    isVisible: (client, profile) => {
      const allowedRoles = [CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN];
      return [profile.role, profile.siteRole].some((role) => allowedRoles.includes(role));
    },
  },
  {
    type: 'ITEM',
    icon: 'Thinkalike',
    name: 'Users',
    color: 'blueLight',
    link: '/users',
    isVisible: (client, profile) => {
      const allowedRoles = [
        CONSTANTS.ROLE.VIEWER,
        CONSTANTS.ROLE.ADMIN,
        CONSTANTS.ROLE.SUPER_ADMIN,
        CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN,
        CONSTANTS.ROLE.CLIENT_SERVICES,
      ];
      return [profile.role, profile.siteRole].some((role) => allowedRoles.includes(role));
    },
  },
  {
    type: 'ITEM',
    icon: 'Onboard',
    name: 'Onboarding',
    color: 'teal',
    link: '/onboarding_landing',
    isHighlighted: (pathname) => pathname.startsWith('/onboarding'),
    isVisible: (client, profile) => {
      const allowedRoles = [CONSTANTS.ROLE.VIEWER, CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN];
      return allowedRoles.includes(profile.role);
    },
  },
  {
    type: 'ITEM',
    icon: 'Crm',
    name: 'Datasets',
    color: 'green',
    link: '/datasets',
    isHighlighted: (pathname) =>
      pathname.startsWith('/datasets') || pathname.startsWith('/segments') || pathname.startsWith('/composer'),
    isVisible: (client, profile) => {
      const allowedRoles = [CONSTANTS.ROLE.VIEWER, CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN];
      return allowedRoles.includes(profile.role);
    },
  },
  {
    type: 'ITEM',
    icon: 'ThinkalikeModel',
    name: <Text>Models</Text>,
    color: 'rioGrande',
    link: ROUTES.MODELS,
    isHighlighted: (pathname) => pathname.startsWith(ROUTES.MODELS),
    isVisible: (client, profile) => {
      const allowedRoles = [CONSTANTS.ROLE.VIEWER, CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN];
      return allowedRoles.includes(profile.role);
    },
  },
  {
    type: 'ITEM',
    icon: 'Activate',
    name: 'Real-Time API',
    color: 'marigold',
    link: '/rt',
    isHighlighted: (pathname) => pathname.startsWith('/rt'),
    isVisible: (client, profile) => {
      const allowedRoles = [CONSTANTS.ROLE.VIEWER, CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN];
      return allowedRoles.includes(profile.role) && client.isRealTimeApiEnabled;
    },
  },
  {
    type: 'ITEM',
    icon: 'Text',
    name: 'Copy Personalization',
    color: 'orange',
    link: '/copypx',
    isHighlighted: (pathname) => pathname.startsWith('/copy'),
    isVisible: (client, profile) => {
      const allowedRoles = [CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.VIEWER, CONSTANTS.ROLE.ADMIN];
      return client.canPersonalize && allowedRoles.includes(profile.role);
    },
  },
  /*
  {
    type: 'ITEM',
    icon: 'Delete',
    name: 'Deleted',
    color: 'red',
    link: '/deleted',
    isHighlighted: (pathname) => (
      pathname.startsWith('/deleted')
    ),
    isVisible: (client, profile) => {
      const allowedRoles = [
        CONSTANTS.ROLE.SUPER_ADMIN,
      ];
      return allowedRoles.includes(profile.role);
    }
  },
  */
];

const accessTokenFilter = ({ accessToken }) => accessToken;

const refreshTokenFilter = ({ refreshToken }) => refreshToken;

const isLoggedInSelector = createSelector(
  [accessTokenFilter, refreshTokenFilter],
  (accessToken, refreshToken) => accessToken && refreshToken
);

function App({
  app: { mini, fetchClientStatus, clients, client, profile, tenant },
  isLoggedIn,
  selectClient,
  toggleMenu,
}) {
  if (isLoggedIn) {
    Sentry.setUser({
      username: profile.username,
      email: profile.email,
      segment: profile.originalTenant,
      ip_address: '{{auto}}',
    });
  }
  return (
    <BrowserRouter>
      {![true, undefined].includes(profile?.username?.includes?.('jenkins')) && (
        <ProductFruits
          workspaceCode="G66U4zUIZtczchW9"
          language="en"
          user={{
            username: profile.username,
            firstname: profile.givenName,
            lastname: profile.familyName,
            email: profile.email,
          }}
        />
      )}
      <GlobalStyle />
      <GLStyle />
      <SplitFactory config={SPLITIO_CONFIG}>
        <ThemeProvider theme={DefaultTheme}>
          <Reset />
          <ErrorBoundary>
            <DndProvider backend={Backend}>
              {!isLoggedIn ? (
                <Switch>
                  <Route exact path="/login" component={Pages.Login} />
                  <Route exact path="/login/:cognitoClientId" component={LoginSSO} />
                  <Route exact path="/password-challenge" component={Pages.PasswordChallenge} />
                  <Route exact path="/forgot-password" component={Pages.ForgotPassword} />
                  <Route exact path="/change-password" component={Pages.ChangePassword} />
                  <Route exact path="/token" component={TokenExchange} />
                  <Route exact path="/token/:cognitoClientId" component={TokenExchange} />
                  <Route
                    path="/"
                    render={() => (
                      <Redirect
                        to={{
                          pathname: '/login',
                          state: { from: window.location.pathname, query: window.location.search },
                        }}
                      />
                    )}
                  />
                </Switch>
              ) : (
                <Application
                  variant="onpoint"
                  header={{
                    mini,
                    zIndex: '10',
                    onToggle: () => toggleMenu(!mini),
                    headerStyle: {
                      fontFamily: 'Roboto',
                    },
                    leftChildren: [
                      <BreadcrumbSwitch key="bc-switch">
                        <BreadcrumbRoute exact path={ROUTES.CLIENTS} component={Breadcrumbs.ClientsBreadcrumb} />
                        <BreadcrumbRoute exact path={ROUTES.CLIENTS_ADD} component={Breadcrumbs.AddClientBreadcrumb} />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.CLIENTS_EDIT}
                          component={Breadcrumbs.EditClientBreadcrumb}
                        />
                        <BreadcrumbRoute exact path={ROUTES.USERS} component={Breadcrumbs.UsersBreadcrumb} />
                        <BreadcrumbRoute exact path={ROUTES.USERS_ADD} component={Breadcrumbs.NewUsersBreadcrumb} />
                        <BreadcrumbRoute exact path={ROUTES.USERS_VIEW} component={Breadcrumbs.EditUsersBreadcrumb} />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.ONBOARDING_LANDING}
                          component={Breadcrumbs.OnboardingLandingBreadcrumb}
                        />
                        <BreadcrumbRoute exact path={ROUTES.ONBOARDING} component={Breadcrumbs.OnboardingBreadcrumb} />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.NEW_ONBOARDING}
                          component={Breadcrumbs.NewOnboardingBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.RE_ONBOARDING}
                          component={Breadcrumbs.ReOnboardingBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.ONBOARDING_VIEW}
                          component={Breadcrumbs.OnboardingViewBreadcrumbView}
                        />
                        <BreadcrumbRoute exact path={ROUTES.SEGMENTS} component={Breadcrumbs.DatasetsBreadcrumb} />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.GENERATE_MODELS}
                          component={Breadcrumbs.GenerateModelsBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.COMPARE_SEGMENTS}
                          component={Breadcrumbs.SegmentsCompareBreadcrumb}
                        />
                        <BreadcrumbRoute exact path={ROUTES.CLUSTERS} component={Breadcrumbs.SegmentsViewBreadcrumb} />
                        <BreadcrumbRoute exact path={ROUTES.COMPOSER} component={Breadcrumbs.SegmentsViewBreadcrumb} />
                        <BreadcrumbRoute exact path={ROUTES.MODELS} component={Breadcrumbs.ModelsListBreadcrumb} />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_LIFT_CHARTS}
                          component={Breadcrumbs.ModelViewBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODELS_COMPARE}
                          component={Breadcrumbs.ModelComparisonBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_INPUT_DISTRIBUTIONS}
                          component={Breadcrumbs.ModelInputDistributionsBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_VALIDATION_ANALYTICS}
                          component={Breadcrumbs.ModelValidationAnalyticsBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_ROI_CALCULATOR}
                          component={Breadcrumbs.ModelRoiCalculatorBreadcrumb}
                        />
                        <BreadcrumbRoute exact path={ROUTES.REAL_TIME_API} component={Breadcrumbs.RtApiBreadcrumb} />
                        <BreadcrumbRoute
                          exact
                          path={[ROUTES.REAL_TIME_API_DOCUMENTATION, ROUTES.REAL_TIME_API_DOCUMENTATION_CONFIG]}
                          component={Breadcrumbs.RtApiDocumentationBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={[ROUTES.REAL_TIME_API_USAGE, ROUTES.REAL_TIME_API_USAGE_CONFIG]}
                          component={Breadcrumbs.RtApiUsageBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.REAL_TIME_API_DEMO}
                          component={Breadcrumbs.RtApiDemoBreadcrumb}
                        />
                        <BreadcrumbRoute exact path={ROUTES.REAL_TIME_API} component={Breadcrumbs.RtApiNewBreadcrumb} />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.COPY_PX}
                          component={Breadcrumbs.CopyPersonalizationBreadcrumb}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.VIEW_SEGMENTS}
                          component={Breadcrumbs.SegmentsViewBreadcrumb}
                        />
                      </BreadcrumbSwitch>,
                    ],
                    rightChildren: [
                      <Icon
                        key="explain-icon"
                        name="InfoSerif"
                        viewBox="0 0 24 24"
                        data-debug="explain-this-page-icon"
                        m={{ x: '1rem' }}
                        size="1.5rem"
                        data-tip="Explain this page"
                        cursor="pointer"
                      />,
                      <Feedback key="feeedback-component" />,
                      <Div
                        minH="1.5rem"
                        minW="1px"
                        border={{ r: '1px solid' }}
                        borderColor="shade66"
                        key="header-spacer"
                      />,
                      clients?.length > 1 ? (
                        <Div d="inline-flex" align="center" key="list-label">
                          <Text m={{ x: '1.5rem' }} textWeight="500" textColor="shade66">
                            Client
                          </Text>
                        </Div>
                      ) : null,
                      <ClientsSelection
                        key="client-selection"
                        loading={fetchClientStatus === CONSTANTS.API.STARTED}
                        clients={clients}
                        tenant={tenant || profile?.tenant}
                        onSelectClient={(c) => selectClient(c)}
                      />,
                      <Icon
                        key="logout-btn"
                        name="Logout"
                        data-tip="Log out"
                        m={{ l: '1.875rem', r: '1.625rem' }}
                        size="1.25rem"
                        hoverColor="brand400"
                        color="shade66"
                        cursor="pointer"
                        onClick={async () => {
                          await inlineLogout();
                          localStorage.removeItem('accessToken');
                          localStorage.removeItem('refreshToken');
                          window.location.href = `${window.location.origin}/login/${
                            localStorage.getItem('login_client') || ''
                          }`;
                        }}
                      />,
                    ],
                  }}
                  menu={{
                    mini,
                    menuStyle: {
                      zIndex: '10',
                      pos: 'absolute',
                      bg: 'shade33',
                    },
                    options: menu.filter(({ isVisible }) => client && profile && isVisible(client, profile)),
                    logo: {
                      largeLogo: <Image src="/static/on-point.svg" />,
                      smallLogo: <Image src="/static/on-point-icon.svg" />,
                    },
                    footerLogo: mini ? null : <Image src="/static/pinpoint.svg" />,
                    footerStyle: {
                      p: { x: mini ? '0rem' : '2rem', y: '2rem' },
                    },
                    isHighlighted: (pathname, link) => pathname.startsWith(link),
                  }}
                  content={{
                    mini,
                    contentStyle: {
                      bg: 'secondary',
                      h: 'calc(100vh - 4.6875rem)',
                    },
                    children: [
                      <Switch key="content-switch">
                        <ProtectedRoute exact path={ROUTES.HOME} component={Pages.Home} />
                        <ProtectedRoute exact path={ROUTES.ONBOARDING_LANDING} component={Pages.OnboardingLanding} />
                        <ProtectedRoute exact path={ROUTES.ONBOARDING_VIEW} component={Pages.OnboardingView} />
                        <ProtectedRoute exact path={ROUTES.ONBOARDING} component={Pages.OnboardingList} />
                        <ProtectedRoute exact path={ROUTES.NEW_ONBOARDING} component={Pages.Onboarding} />
                        <ProtectedRoute exact path={ROUTES.RE_ONBOARDING} component={Pages.ReOnboarding} />
                        <Route exact path={ROUTES.DATA_ENRICHMENT_DOWNLOAD} component={Pages.DataEnrichmentDownload} />
                        <ProtectedRoute exact path={ROUTES.GENERATE_MODELS} component={Pages.GenerateModels} />
                        <ProtectedRoute exact path="/agencies" component={Pages.Agencies} />
                        <ProtectedRoute exact path="/agencies/add" component={Pages.AddAgency} />
                        <ProtectedRoute exact path="/agencies/:agencyId" component={Pages.EditAgency} />
                        <ProtectedRoute exact path={ROUTES.USERS} component={Pages.Users} />
                        <ProtectedRoute exact path={ROUTES.USERS_ADD} component={Pages.AddUser} />
                        <ProtectedRoute exact path={ROUTES.USERS_VIEW} component={Pages.EditUser} />
                        <ProtectedRoute exact path={ROUTES.SEGMENTS} component={Pages.Segments} />
                        <ProtectedRoute exact path={ROUTES.COMPARE_SEGMENTS} component={Pages.CompareSegments} />
                        <ProtectedRoute
                          exact
                          path="/segments/:segmentId/pred-model"
                          component={Pages.SegmentsPredModelView}
                        />
                        <ProtectedRoute
                          exact
                          path="/segments/:segmentId/facets-view"
                          component={Pages.SegmentsFacetsView}
                        />
                        <ProtectedRoute
                          exact
                          path="/segments/:segmentId/personality-view"
                          component={Pages.SegmentsOceanView}
                        />
                        <ProtectedRoute exact path={ROUTES.CLUSTERS} component={Pages.Clusters} />
                        <ProtectedRoute exact path={ROUTES.MODELS} component={Pages.ListModelsPage} />
                        <ProtectedRoute exact path={ROUTES.MODELS_COMPARE} component={Pages.CompareModelsPage} />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_INPUT_DISTRIBUTIONS}
                          component={Pages.InputDistributionsModelsPage}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_VALIDATION_ANALYTICS}
                          component={Pages.ValidationAnalyticsModelsPage}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_ROI_CALCULATOR}
                          component={Pages.RoiCalculatorModelsPage}
                        />
                        <ProtectedRoute exact path={ROUTES.MODEL_LIFT_CHARTS} component={Pages.LiftChartsModelsPage} />
                        <ProtectedRoute
                          exact
                          path={[ROUTES.REAL_TIME_API_USAGE, ROUTES.REAL_TIME_API_USAGE_CONFIG]}
                          component={Pages.RtApiUsage}
                        />
                        <ProtectedRoute
                          exact
                          path={[ROUTES.REAL_TIME_API_DOCUMENTATION, ROUTES.REAL_TIME_API_DOCUMENTATION_CONFIG]}
                          component={Pages.RtApiDocumentation}
                        />
                        <ProtectedRoute exact path={ROUTES.REAL_TIME_API} component={Pages.RtApiList} />
                        <ProtectedRoute exact path={ROUTES.REAL_TIME_API_DEMO} component={Pages.RtApiDemo} />
                        <ProtectedRoute exact path={ROUTES.COPY_PX} component={Pages.CopyPx} />
                        <ProtectedRoute exact path="/composer/:datasetId" component={Pages.Composer} />
                        <ProtectedRoute exact path="/story/:datasetId" component={Pages.Story} />
                        <ProtectedRoute exact path={ROUTES.CLIENTS} component={Pages.Clients} />
                        <ProtectedRoute exact path={ROUTES.CLIENTS_ADD} component={Pages.AddClient} />
                        <ProtectedRoute exact path={ROUTES.CLIENTS_EDIT} component={Pages.EditClient} />
                        <ProtectedRoute exact path={ROUTES.VIEW_SEGMENTS} component={Pages.SegmentsView} />
                        <Route exact path="/token" component={TokenExchange} />
                        <Route path="/" render={() => <Redirect to="/onboarding_landing" />} />
                      </Switch>,
                    ],
                  }}
                />
              )}
            </DndProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </SplitFactory>
    </BrowserRouter>
  );
}

App.propTypes = {
  app: PropTypes.shape({
    mini: PropTypes.bool.isRequired,
    fetchClientStatus: PropTypes.bool.isRequired,
    clients: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ).isRequired,
    profile: PropTypes.shape({
      email: PropTypes.string,
      username: PropTypes.string,
      tenant: PropTypes.string,
      originalTenant: PropTypes.string,
      role: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
    }).isRequired,
    client: PropTypes.shape({}),
    tenant: PropTypes.string,
  }).isRequired,
  auth: PropTypes.shape({
    challengeName: PropTypes.string,
  }).isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  selectClient: PropTypes.func.isRequired,
  toggleMenu: PropTypes.func.isRequired,
};

const mapStateToProps = ({ auth, app }) => ({
  app,
  auth,
  isLoggedIn: isLoggedInSelector(auth),
});

const mapDispatchToProps = (dispatch) => ({
  toggleMenu: () => dispatch(appActions.toggleMenu()),
  selectClient: (client) => dispatch(appActions.selectClient(client)),
});

export default withSplitFactory(SPLITIO_CONFIG)(connect(mapStateToProps, mapDispatchToProps)(App));
